import React, { useEffect, useMemo, useState } from "react";
import {
  PlusIcon,
  TrashIcon,
  PencilSquareIcon,
  TrophyIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  SelectItem,
  useDisclosure,
  Card,
  CardBody,
  DatePicker,
} from "@heroui/react";
import { parseDate, today, getLocalTimeZone } from "@internationalized/date";
import AppSelect from "../../components/common/appSelect";
import { FightHistoryDto } from "../../types/types";
import { formatAlltext } from "../../utils/common/formatUtils";

const safeParseDate = (value?: string | null) => {
  if (!value) return null;

  const datePart = value.split("T")[0];

  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    return null;
  }

  try {
    return parseDate(datePart);
  } catch {
    return null;
  }
};

interface FightHistoryProps {
  initialFightHistories?: FightHistoryDto[];
  onFightHistoriesChange: (fightHistories: FightHistoryDto[]) => void;
  locations: string[];
  canEdit?: boolean;
}

const FightHistory: React.FC<FightHistoryProps> = ({
  initialFightHistories,
  onFightHistoriesChange,
  locations = [],
  canEdit = false,
}) => {
  const { t } = useTranslation("fighthistory");
  const { isOpen, onOpen, onOpenChange } = useDisclosure();

  const [fightHistories, setFightHistories] = useState<FightHistoryDto[]>(
    initialFightHistories ?? []
  );

  const [formData, setFormData] = useState<FightHistoryDto>({
    opponentName: "",
    date: "",
    location: "",
    result: "",
    round: 1,
    typeOfEnding: "",
    organizationName: "",
    preferredRuleSet: "",
  });

  const [formErrors, setFormErrors] = useState<
    Partial<Record<keyof FightHistoryDto, string>>
  >({});

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);

  useEffect(() => {
    setFightHistories(initialFightHistories ?? []);
  }, [initialFightHistories]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && !target.closest("[data-location-wrapper='true']")) {
        setLocationDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const preferredRuleSetOptions = [
    { key: "AMETEUR", label: t("Amateur") },
    { key: "SEMI_PRO", label: t("SemiPro") },
    { key: "PROFESSIONAL", label: t("Professional") },
  ];

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof FightHistoryDto, string>> = {};
    if (!formData.opponentName.trim())
      errors.opponentName = t("opponentNameRequired");
    if (!safeParseDate(formData.date)) errors.date = t("dateRequired");
    if (!formData.location.trim()) errors.location = t("locationRequired");
    if (!formData.result.trim()) errors.result = t("resultRequired");
    if (!formData.round || formData.round < 1) {
      errors.round = t("roundRequiredOrInvalid");
    }
    if (!formData.typeOfEnding) errors.typeOfEnding = t("typeOfEndingRequired");
    if (!formData.preferredRuleSet)
      errors.preferredRuleSet = t("preferredRuleSetRequired");

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      opponentName: "",
      date: "",
      location: "",
      result: "",
      round: 0,
      typeOfEnding: "",
      organizationName: "",
      preferredRuleSet: "",
    });
    setFormErrors({});
    setEditingIndex(null);
    setLocationDropdownOpen(false);
  };

  const handleInputChange = (name: string, value: string) => {
    let processedValue = value;
    if (name === "opponentName" && value.length > 25) {
      processedValue = value.substring(0, 25);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: name === "round" ? Number(processedValue) || 0 : processedValue,
    }));

    setFormErrors((prev) => ({ ...prev, [name]: undefined }));
  };

 const handleDateChange = (date: any) => {
  handleInputChange("date", date ? date.toString() : "");
};

  const openAddModal = () => {
    resetForm();
    onOpen();
  };

  const openEditModal = (index: number) => {
    setFormData(fightHistories[index]);
    setEditingIndex(index);
    onOpen();
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const cleanedData = {
      ...formData,
      organizationName: formData.organizationName?.trim() || undefined,
      preferredRuleSet: formData.preferredRuleSet || undefined,
    };

    let updated: FightHistoryDto[];
    if (editingIndex !== null) {
      updated = [...fightHistories];
      updated[editingIndex] = cleanedData;
    } else {
      updated = [...fightHistories, cleanedData];
    }

    setFightHistories(updated);
    onFightHistoriesChange(updated);
    resetForm();
    onOpenChange();
  };

  const handleRemoveFight = (index: number) => {
    const updated = fightHistories.filter((_, i) => i !== index);
    setFightHistories(updated);
    onFightHistoriesChange(updated);
  };

  const getResultColor = (result: string) => {
    const lower = result.toLowerCase();
    if (lower.includes("win") || lower.includes("victory")) return "success";
    if (lower.includes("loss") || lower.includes("defeat")) return "danger";
    if (lower.includes("draw")) return "warning";
    return "default";
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  const resultOptions = [
    { key: "Win", label: t("win") },
    { key: "Loss", label: t("loss") },
    { key: "Draw", label: t("draw") },
  ];

  const typeOfEndingOptions = [
    { key: "KO", label: t("KO") },
    { key: "TKO", label: t("TKO") },
    { key: "Submission", label: t("Submission") },
    { key: "Decision", label: t("Decision") },
    { key: "Draw", label: t("Draw") },
    { key: "DQ", label: t("DQ") },
    { key: "No_Contest", label: t("No_Contest") },
  ];

  const filteredLocations = useMemo(() => {
    const q = formData.location.trim().toLowerCase();
    if (!q) return locations;
    return locations.filter((location) =>
      location.toLowerCase().includes(q)
    );
  }, [locations, formData.location]);

  return (
    <div className="w-full space-y-3">
      <div className="mt-4 flex justify-end items-center">
        <Button
          color="primary"
          onPress={openAddModal}
          startContent={<PlusIcon className="h-3 w-3" />}
          className="font-medium text-xs"
          size="sm"
          radius="lg"
        >
          {t("addFightHistory")}
        </Button>
      </div>

      {fightHistories.length ? (
        <div className="space-y-3">
          {fightHistories.map((history, index) => (
            <Card
              key={index}
              className="w-full group relative border border-default-200 hover:shadow-md transition-shadow duration-200 rounded-xl"
            >
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                {canEdit && (
                  <Button
                    isIconOnly
                    variant="light"
                    color="primary"
                    onPress={() => openEditModal(index)}
                    size="sm"
                    className="h-7 w-7"
                  >
                    <PencilSquareIcon className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  isIconOnly
                  variant="light"
                  color="danger"
                  onPress={() => handleRemoveFight(index)}
                  size="sm"
                  className="h-7 w-7"
                >
                  <TrashIcon className="h-4 w-4" />
                </Button>
              </div>

              <CardBody className="p-4 space-y-4">
                <div className="grid grid-cols-4 gap-4 items-center">
                  <div className="col-span-2">
                    <div className="text-xs text-gray-600 mb-1">
                      {t("enterOpponentName")}
                    </div>
                    <div className="font-medium text-default-700 text-sm">
                      {history.opponentName}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600 mb-1">{t("date")}</div>
                    <div className="font-medium text-default-700 text-sm">
                      {formatDate(history.date)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600 mb-1">{t("round")}</div>
                    <div className="font-medium text-default-700 text-sm">
                      {history.round}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4 items-center">
                  <div>
                    <div className="text-xs text-gray-600 mb-1">
                      {t("organization")}
                    </div>
                    <div className="font-medium text-default-700 text-sm">
                      {history.organizationName || "-"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600 mb-1">{t("Location")}</div>
                    <div className="font-medium text-default-700 text-sm">
                      {history.location}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600 mb-1">{t("method")}</div>
                    <div className="font-medium text-default-700 text-sm">
                      {t(history.typeOfEnding.toLowerCase()) ||
                        history.typeOfEnding}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600 mb-1">{t("result")}</div>
                    <span
                      className={`text-xs font-semibold px-2 py-1 rounded-full
                        ${
                          getResultColor(history.result) === "success"
                            ? "bg-success-100 text-success-700"
                            : getResultColor(history.result) === "danger"
                              ? "bg-danger-100 text-danger-700"
                              : getResultColor(history.result) === "warning"
                                ? "bg-warning-100 text-warning-700"
                                : "bg-default-100 text-default-700"
                        }`}
                    >
                      {history.result}
                    </span>
                  </div>
                </div>

                {history.preferredRuleSet && (
                  <div>
                    <div className="text-xs text-gray-600 mb-1">
                      {t("competitionLevel")}
                    </div>
                    <div className="font-medium text-default-700 text-sm">
                      {t(history.preferredRuleSet.toLowerCase()) !==
                      history.preferredRuleSet.toLowerCase()
                        ? t(history.preferredRuleSet.toLowerCase())
                        : formatAlltext(history.preferredRuleSet)}
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="w-full">
          <CardBody className="text-center py-10">
            <div className="flex flex-col items-center gap-4">
              <div className="p-3 rounded-full bg-default-100">
                <TrophyIcon className="h-6 w-6 text-default-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  {t("noFightHistory")}
                </h3>
                <p className="text-xs text-default-500">{t("addFirstFight")}</p>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      <Modal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        onClose={resetForm}
        placement="center"
        size="2xl"
        scrollBehavior="inside"
        classNames={{
          base: "bg-background",
          header: "border-b border-divider px-4 py-3",
          body: "px-4 py-4",
          footer: "border-t border-divider px-4 py-3",
          wrapper: "border border-default-300",
        }}
      >
        <ModalContent>
          {(onClose: () => void) => (
            <>
              <ModalHeader className="flex flex-col gap-1 p-4">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-primary-50">
                    <TrophyIcon className="h-4 w-4 text-primary-600" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold">
                      {editingIndex === null
                        ? t("addFightHistory")
                        : t("editFightHistory")}
                    </h2>
                    <p className="text-xs text-default-500 font-normal">
                      {editingIndex === null
                        ? t("fillFightDetails")
                        : t("updateFightDetails")}
                    </p>
                  </div>
                </div>
              </ModalHeader>

              <ModalBody className="gap-3 py-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-default-600 mb-1 block">
                      {t("opponentName")}
                    </label>
                    <Input
                      placeholder={t("enterOpponentName")}
                      value={formData.opponentName}
                      onValueChange={(v: string) =>
                        handleInputChange("opponentName", v)
                      }
                      isInvalid={!!formErrors.opponentName}
                      errorMessage={formErrors.opponentName}
                      isRequired
                      variant="bordered"
                      size="sm"
                      radius="lg"
                      description={`${formData.opponentName.length}/25`}
                      maxLength={25}
                      classNames={{
                        input: "text-xs h-8",
                        inputWrapper: "h-8",
                        description: "text-xs",
                      }}
                    />
                  </div>

                  <div>
                    <label className="text-xs text-default-600 mb-1 block">
                      {t("date")}
                    </label>
                    <DatePicker
                      value={safeParseDate(formData.date)}
                      onChange={handleDateChange}
                      isInvalid={!!formErrors.date}
                      errorMessage={formErrors.date}
                      isRequired
                      variant="bordered"
                      size="sm"
                      radius="lg"
                      maxValue={today(getLocalTimeZone())}
                      classNames={{
                        input: "text-xs h-8",
                        inputWrapper: "h-8",
                      }}
                    />
                  </div>

                  <div>
                    <label className="text-xs text-default-600 mb-1 block">
                      {t("result")}
                    </label>
                    <AppSelect
                      placeholder={t("selectResult")}
                      selectedKeys={formData.result ? [formData.result] : []}
                      onSelectionChange={(keys: any) => {
                        const val = Array.from(keys)[0] as string;
                        handleInputChange("result", val);
                      }}
                      isInvalid={!!formErrors.result}
                      errorMessage={formErrors.result}
                      isRequired
                      variant="bordered"
                      size="sm"
                      radius="lg"
                      classNames={{ value: "text-xs", trigger: "h-8" }}
                    >
                      {resultOptions.map((o) => (
                        <SelectItem key={o.key} className="text-xs">
                          {o.label}
                        </SelectItem>
                      ))}
                    </AppSelect>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div data-location-wrapper="true" className="relative">
                    <label className="text-xs text-default-600 mb-1 block">
                      {t("Location")}
                    </label>
                    <Input
                      placeholder={t("selectLocation")}
                      value={formData.location}
                      onValueChange={(value: string) => {
                        handleInputChange("location", value);
                        setLocationDropdownOpen(true);
                      }}
                      onFocus={() => setLocationDropdownOpen(true)}
                      onBlur={() => {
                        window.setTimeout(() => {
                          setLocationDropdownOpen(false);
                        }, 150);
                      }}
                      isInvalid={!!formErrors.location}
                      errorMessage={formErrors.location}
                      isRequired
                      variant="bordered"
                      size="sm"
                      radius="lg"
                      classNames={{ input: "text-xs h-8", inputWrapper: "h-8" }}
                    />

                    {locationDropdownOpen && (
                      <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-lg border border-default-200 bg-white shadow-lg">
                        <div className="max-h-48 overflow-auto">
                          {filteredLocations.length > 0 ? (
                            filteredLocations.map((location) => (
                              <button
                                key={location}
                                type="button"
                                className="w-full px-3 py-2 text-left text-xs hover:bg-default-100"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  handleInputChange("location", location);
                                  setLocationDropdownOpen(false);
                                }}
                              >
                                {location}
                              </button>
                            ))
                          ) : (
                            <div className="px-3 py-2 text-xs text-default-500">
                              {t("noLocationsAvailable")}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="text-xs text-default-600 mb-1 block">
                      {t("round")}
                    </label>
                    <Input
                      type="number"
                      placeholder={t("enterRoundNumber")}
                      value={formData.round.toString()}
                      onValueChange={(v: string) =>
                        handleInputChange("round", v)
                      }
                      isInvalid={!!formErrors.round}
                      errorMessage={formErrors.round}
                      min="1"
                      isRequired
                      variant="bordered"
                      size="sm"
                      radius="lg"
                      classNames={{ input: "text-xs h-8", inputWrapper: "h-8" }}
                    />
                  </div>

                  <div>
                    <label className="text-xs text-default-600 mb-1 block">
                      {t("method")}
                    </label>
                    <AppSelect
                      placeholder={t("selectTypeOfEnding")}
                      selectedKeys={
                        formData.typeOfEnding ? [formData.typeOfEnding] : []
                      }
                      onSelectionChange={(keys: any) => {
                        const val = Array.from(keys)[0] as string;
                        handleInputChange("typeOfEnding", val);
                      }}
                      isInvalid={!!formErrors.typeOfEnding}
                      errorMessage={formErrors.typeOfEnding}
                      isRequired
                      variant="bordered"
                      size="sm"
                      radius="lg"
                      classNames={{ value: "text-xs", trigger: "h-8" }}
                    >
                      {typeOfEndingOptions.map((o) => (
                        <SelectItem key={o.key} className="text-xs">
                          {o.label}
                        </SelectItem>
                      ))}
                    </AppSelect>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-default-600 mb-1 block">
                      {t("organization")}
                    </label>
                    <Input
                      placeholder={t("enterOrganizationName")}
                      value={formData.organizationName || ""}
                      onValueChange={(v: string) =>
                        handleInputChange("organizationName", v)
                      }
                      variant="bordered"
                      description={t("optional")}
                      size="sm"
                      radius="lg"
                      classNames={{
                        input: "text-xs h-8",
                        inputWrapper: "h-8",
                        description: "text-xs",
                      }}
                    />
                  </div>

                  <div>
                    <label className="text-xs text-default-600 mb-1 block">
                      {t("competitionLevel")}
                    </label>
                    <AppSelect
                      placeholder={t("selectPreferredRuleSet")}
                      selectedKeys={
                        formData.preferredRuleSet
                          ? [formData.preferredRuleSet]
                          : []
                      }
                      onSelectionChange={(keys: any) => {
                        const val = Array.from(keys)[0] as string;
                        handleInputChange("preferredRuleSet", val);
                      }}
                      isInvalid={!!formErrors.preferredRuleSet}
                      errorMessage={formErrors.preferredRuleSet}
                      isRequired
                      variant="bordered"
                      size="sm"
                      radius="lg"
                      classNames={{ value: "text-xs", trigger: "h-8" }}
                    >
                      {preferredRuleSetOptions.map((o) => (
                        <SelectItem key={o.key} className="text-xs">
                          {o.label}
                        </SelectItem>
                      ))}
                    </AppSelect>
                  </div>
                </div>
              </ModalBody>

              <ModalFooter className="gap-2 p-4">
                <Button
                  color="default"
                  variant="light"
                  onPress={onClose}
                  size="sm"
                  radius="lg"
                  className="text-xs"
                >
                  {t("cancel")}
                </Button>
                <Button
                  color="primary"
                  onPress={handleSubmit}
                  className="font-medium text-xs"
                  size="sm"
                  radius="lg"
                >
                  {editingIndex === null ? t("submit") : t("update")}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};

export default FightHistory;